import React, { useState } from 'react';
import { Calculator, DollarSign, TrendingUp, ArrowRight, Share2, Info, MapPin } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';

// 2024-2025 Federal Tax Brackets (same for all provinces)
const federalBrackets = [
  { min: 0, max: 55867, rate: 0.15 },
  { min: 55867, max: 111733, rate: 0.205 },
  { min: 111733, max: 173205, rate: 0.26 },
  { min: 173205, max: 246752, rate: 0.29 },
  { min: 246752, max: Infinity, rate: 0.33 },
];

// Quebec Provincial Tax Brackets (2024-2025)
const quebecBrackets = [
  { min: 0, max: 51780, rate: 0.14 },
  { min: 51780, max: 103545, rate: 0.19 },
  { min: 103545, max: 126000, rate: 0.24 },
  { min: 126000, max: Infinity, rate: 0.2575 },
];

// Ontario Provincial Tax Brackets (2024-2025)
const ontarioBrackets = [
  { min: 0, max: 51446, rate: 0.0505 },
  { min: 51446, max: 102894, rate: 0.0915 },
  { min: 102894, max: 150000, rate: 0.1116 },
  { min: 150000, max: 220000, rate: 0.1216 },
  { min: 220000, max: Infinity, rate: 0.1316 },
];

// Basic Personal Amounts (2024-2025)
const federalBPA = 15705;
const quebecBPA = 18056;
const ontarioBPA = 12399;

// Quebec federal abatement (16.5% reduction on federal tax)
const quebecAbatement = 0.165;

const calculateTax = (income, brackets, bpa) => {
  const taxableIncome = Math.max(0, income - bpa);
  let tax = 0;
  let remainingIncome = taxableIncome;

  for (const bracket of brackets) {
    if (remainingIncome <= 0) break;
    const taxableInBracket = Math.min(remainingIncome, bracket.max - bracket.min);
    tax += taxableInBracket * bracket.rate;
    remainingIncome -= taxableInBracket;
  }

  return tax;
};

const calculateOntarioSurtax = (provincialTax) => {
  // Ontario surtax: 20% on provincial tax over $5,554, plus 36% on provincial tax over $7,108
  let surtax = 0;
  if (provincialTax > 5554) {
    surtax += (provincialTax - 5554) * 0.20;
  }
  if (provincialTax > 7108) {
    surtax += (provincialTax - 7108) * 0.36;
  }
  return surtax;
};

export const SalaryComparisonPage = () => {
  const { toast } = useToast();
  const [salary, setSalary] = useState('');
  const [showResults, setShowResults] = useState(false);

  const handleCalculate = () => {
    const salaryNum = parseFloat(salary.replace(/,/g, ''));
    if (isNaN(salaryNum) || salaryNum <= 0) {
      toast({
        title: "Invalid Salary",
        description: "Please enter a valid annual salary.",
        variant: "destructive"
      });
      return;
    }
    setShowResults(true);
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-CA', { 
      style: 'currency', 
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    }).format(num);
  };

  const formatSalaryInput = (value) => {
    const num = value.replace(/[^0-9]/g, '');
    if (num) {
      return new Intl.NumberFormat('en-CA').format(parseInt(num));
    }
    return '';
  };

  const calculateResults = () => {
    const grossSalary = parseFloat(salary.replace(/,/g, ''));

    // Federal tax calculation
    const federalTaxBase = calculateTax(grossSalary, federalBrackets, federalBPA);
    
    // Quebec calculations
    const quebecProvincialTax = calculateTax(grossSalary, quebecBrackets, quebecBPA);
    const quebecFederalTax = federalTaxBase * (1 - quebecAbatement); // 16.5% abatement
    const quebecTotalTax = quebecFederalTax + quebecProvincialTax;
    const quebecTakeHome = grossSalary - quebecTotalTax;

    // Ontario calculations
    const ontarioProvincialTax = calculateTax(grossSalary, ontarioBrackets, ontarioBPA);
    const ontarioSurtax = calculateOntarioSurtax(ontarioProvincialTax);
    const ontarioTotalProvincial = ontarioProvincialTax + ontarioSurtax;
    const ontarioTotalTax = federalTaxBase + ontarioTotalProvincial;
    const ontarioTakeHome = grossSalary - ontarioTotalTax;

    // Difference
    const annualSavings = ontarioTakeHome - quebecTakeHome;
    const monthlySavings = annualSavings / 12;
    const percentMoreTakeHome = ((annualSavings / quebecTakeHome) * 100);

    return {
      grossSalary,
      quebec: {
        federalTax: quebecFederalTax,
        provincialTax: quebecProvincialTax,
        totalTax: quebecTotalTax,
        takeHome: quebecTakeHome,
        effectiveRate: (quebecTotalTax / grossSalary) * 100,
      },
      ontario: {
        federalTax: federalTaxBase,
        provincialTax: ontarioTotalProvincial,
        totalTax: ontarioTotalTax,
        takeHome: ontarioTakeHome,
        effectiveRate: (ontarioTotalTax / grossSalary) * 100,
      },
      savings: {
        annual: annualSavings,
        monthly: monthlySavings,
        percent: percentMoreTakeHome,
      }
    };
  };

  const results = showResults ? calculateResults() : null;

  const handleShare = async () => {
    if (!results) return;
    const shareText = `Moving from Quebec to Ontario (Cornwall), I'd take home ${formatCurrency(results.savings.annual)} MORE per year on a ${formatCurrency(results.grossSalary)} salary! That's ${results.savings.percent.toFixed(1)}% more in my pocket. Calculate yours: `;
    const shareUrl = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({ title: 'My Take-Home Pay Comparison', text: shareText, url: shareUrl });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(shareText + shareUrl);
      toast({ title: "Link Copied!", description: "Share link copied to clipboard." });
    }
  };

  const presetSalaries = [50000, 75000, 100000, 125000, 150000];

  return (
    <section className="pt-24 pb-20 bg-gradient-to-br from-slate-50 to-purple-50 min-h-screen" data-testid="salary-comparison-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-purple-100 px-4 py-2 rounded-full mb-4">
            <Calculator className="w-5 h-5 text-purple-700" />
            <span className="text-sm font-semibold text-purple-700">Salary Comparison Calculator</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">
            Compare Your Take-Home Pay
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            See how much more money you'd keep by moving from Quebec to Ontario (Cornwall)
          </p>
        </div>

        {/* Salary Input */}
        <Card className="border-2 border-purple-200 mb-8">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-purple-600" />
              Enter Your Annual Gross Salary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-slate-400">$</span>
                <input
                  type="text"
                  value={salary}
                  onChange={(e) => {
                    setSalary(formatSalaryInput(e.target.value));
                    setShowResults(false);
                  }}
                  placeholder="Enter your salary"
                  className="w-full pl-12 pr-4 py-4 text-2xl font-bold border-2 border-slate-300 rounded-xl focus:outline-none focus:border-purple-500 transition-colors"
                  data-testid="salary-input"
                />
              </div>

              {/* Quick Select */}
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-slate-500 mr-2 self-center">Quick select:</span>
                {presetSalaries.map((preset) => (
                  <button
                    key={preset}
                    onClick={() => {
                      setSalary(formatSalaryInput(preset.toString()));
                      setShowResults(false);
                    }}
                    className={`px-4 py-2 rounded-lg border-2 transition-all ${
                      salary === formatSalaryInput(preset.toString())
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-slate-200 hover:border-purple-300 text-slate-600'
                    }`}
                  >
                    {formatCurrency(preset)}
                  </button>
                ))}
              </div>

              {!showResults && (
                <Button
                  onClick={handleCalculate}
                  size="lg"
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg"
                  data-testid="calculate-salary-btn"
                >
                  <Calculator className="w-5 h-5 mr-2" />
                  Calculate My Take-Home Pay
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {showResults && results && (
          <div className="space-y-8 animate-in fade-in duration-500">
            {/* Main Savings Card */}
            <Card className="border-4 border-green-400 bg-gradient-to-br from-green-50 to-emerald-50 overflow-hidden">
              <CardContent className="pt-8 pb-8">
                <div className="text-center space-y-4">
                  <Badge className="bg-green-600 text-white text-sm px-4 py-1">
                    Your Extra Take-Home Pay in Ontario
                  </Badge>
                  <div className="space-y-2">
                    <div className="text-6xl sm:text-7xl font-bold text-green-600">
                      {formatCurrency(results.savings.annual)}
                    </div>
                    <div className="text-xl text-slate-600">more per year</div>
                  </div>
                  <div className="flex justify-center gap-6 text-lg flex-wrap">
                    <div>
                      <span className="font-bold text-green-600">{formatCurrency(results.savings.monthly)}</span>
                      <span className="text-slate-600"> /month extra</span>
                    </div>
                    <div>
                      <span className="font-bold text-green-600">{results.savings.percent.toFixed(1)}%</span>
                      <span className="text-slate-600"> more in your pocket</span>
                    </div>
                  </div>
                  
                  <div className="pt-4">
                    <Button
                      onClick={handleShare}
                      variant="outline"
                      className="border-green-400 text-green-700 hover:bg-green-100"
                      data-testid="share-salary-btn"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share Your Results
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Side by Side Comparison */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Quebec */}
              <Card className="border-2 border-red-200">
                <CardHeader className="bg-red-50">
                  <CardTitle className="text-xl flex items-center gap-2 text-red-800">
                    <MapPin className="w-5 h-5" />
                    Quebec (Montreal)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Gross Salary</span>
                    <span className="font-semibold">{formatCurrency(results.grossSalary)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Federal Tax</span>
                    <span className="text-red-600">-{formatCurrency(results.quebec.federalTax)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Provincial Tax</span>
                    <span className="text-red-600">-{formatCurrency(results.quebec.provincialTax)}</span>
                  </div>
                  <div className="border-t-2 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Total Tax</span>
                      <span className="font-bold text-red-600">-{formatCurrency(results.quebec.totalTax)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-slate-500 mt-1">
                      <span>Effective Tax Rate</span>
                      <span>{results.quebec.effectiveRate.toFixed(1)}%</span>
                    </div>
                  </div>
                  <div className="bg-red-100 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-slate-700">Take-Home Pay</span>
                      <span className="text-2xl font-bold text-red-700">{formatCurrency(results.quebec.takeHome)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ontario */}
              <Card className="border-2 border-green-200">
                <CardHeader className="bg-green-50">
                  <CardTitle className="text-xl flex items-center gap-2 text-green-800">
                    <MapPin className="w-5 h-5" />
                    Ontario (Cornwall)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Gross Salary</span>
                    <span className="font-semibold">{formatCurrency(results.grossSalary)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Federal Tax</span>
                    <span className="text-red-600">-{formatCurrency(results.ontario.federalTax)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Provincial Tax</span>
                    <span className="text-green-600">-{formatCurrency(results.ontario.provincialTax)}</span>
                  </div>
                  <div className="border-t-2 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Total Tax</span>
                      <span className="font-bold text-slate-700">-{formatCurrency(results.ontario.totalTax)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-slate-500 mt-1">
                      <span>Effective Tax Rate</span>
                      <span>{results.ontario.effectiveRate.toFixed(1)}%</span>
                    </div>
                  </div>
                  <div className="bg-green-100 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-slate-700">Take-Home Pay</span>
                      <span className="text-2xl font-bold text-green-700">{formatCurrency(results.ontario.takeHome)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* What You Could Do Card */}
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-xl">What Could You Do With {formatCurrency(results.savings.annual)} Extra Per Year?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="bg-white p-4 rounded-lg border text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-1">
                      {Math.floor(results.savings.annual / 500)}
                    </div>
                    <div className="text-sm text-slate-600">Extra mortgage payments</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-1">
                      {formatCurrency(results.savings.annual * 5)}
                    </div>
                    <div className="text-sm text-slate-600">After 5 years of savings</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-1">
                      {Math.floor(results.savings.annual / 2000)}
                    </div>
                    <div className="text-sm text-slate-600">Family vacations</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-1">
                      {formatCurrency(results.savings.monthly)}
                    </div>
                    <div className="text-sm text-slate-600">Monthly RRSP contribution</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Benefits */}
            <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                  Plus: Cost of Living Savings in Cornwall
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 mb-4">
                  In addition to tax savings, Cornwall offers significantly lower cost of living compared to Montreal:
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg border-2 border-emerald-200">
                    <div className="text-2xl font-bold text-emerald-600">$350/mo</div>
                    <div className="text-sm text-slate-600">Lower housing costs</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border-2 border-emerald-200">
                    <div className="text-2xl font-bold text-emerald-600">$377K</div>
                    <div className="text-sm text-slate-600">Average home price</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border-2 border-emerald-200">
                    <div className="text-2xl font-bold text-emerald-600">15%</div>
                    <div className="text-sm text-slate-600">Overall cost savings</div>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
                    <a href="/cost-calculator">
                      <Calculator className="w-4 h-4 mr-2" />
                      Calculate Full Cost of Living Savings
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* CTA */}
            <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50">
              <CardContent className="pt-8 pb-8 text-center space-y-4">
                <h3 className="text-2xl font-bold text-slate-900">Ready to Keep More of Your Money?</h3>
                <p className="text-slate-600 max-w-xl mx-auto">
                  Explore Cornwall's housing market, neighborhoods, and community to plan your move.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild className="bg-purple-600 hover:bg-purple-700">
                    <a href="/housing-market">View Housing Market</a>
                  </Button>
                  <Button asChild variant="outline" className="border-purple-400 text-purple-700">
                    <a href="/explore">Explore Cornwall</a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Try Different Salary */}
            <div className="text-center">
              <Button
                variant="ghost"
                onClick={() => setShowResults(false)}
                className="text-slate-600"
              >
                ← Try a different salary
              </Button>
            </div>
          </div>
        )}

        {/* Info Box */}
        <Card className="mt-8 border border-slate-200 bg-slate-50">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-500 space-y-2">
                <p>
                  <strong>How this works:</strong> This calculator uses 2024-2025 federal and provincial income tax brackets 
                  to estimate your take-home pay. Quebec residents receive a 16.5% federal tax abatement, but still pay 
                  significantly higher provincial taxes (14-25.75%) compared to Ontario (5.05-13.16%).
                </p>
                <p>
                  <strong>Note:</strong> This is an estimate for comparison purposes. Actual taxes may vary based on 
                  deductions, credits, CPP/QPP contributions, EI premiums, and other factors. Consult a tax professional 
                  for personalized advice.
                </p>
                <p className="text-xs">
                  Sources: Canada Revenue Agency, Revenu Québec, Ontario Ministry of Finance (2024-2025 tax rates)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default SalaryComparisonPage;
